import React, { useState, useEffect, useRef } from "react";

const ProfessionalTimeline = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const timelineRef = useRef(null);

  const timelineData = [
    {
      id: "dendera-studios",
      year: "July 2025 - Present",
      title: "Lead Web Strategist",
      company: "Dendera Studios",
      type: "current",
      description:
        "I lead end to end web strategy and execution for early-stage and growing businesses, transforming ideas with no prior digital footprint into fully functional, revenue-generating platforms. My work spans leading stakeholder meetings, product definition, UX strategy, and technical implementation, with a strong emphasis on scalability, performance, and conversion.",
      achievements: [
        "Partner with founders to define product vision, scope, and success metrics",
        "Design UX strategy and information architecture for 0-to-1 web experiences",
        "Lead technical implementation with focus on scalability, performance, and SEO",
        "Optimize key flows for conversion, from landing pages to checkout journeys",
      ],
      technologies: [
        "Web Strategy",
        "Product Definition",
        "UX Strategy",
        "Technical Implementation",
      ],
      icon: "🚀",
    },

    {
      id: "24hour-current",
      year: "Nov 2023 - Present",
      title: "Sales and Service Expert",
      company: "24 Hour Fitness", 
      type: "current",
      description: "Leading customer experience initiatives while contributing to UX design projects. Combining sales expertise with design thinking to optimize member journey and business outcomes.",
      achievements: [
        "Top ten SSE in fitness revenue for month of April",
        "Assisted team to increase NPS from 40 to 63",
        "Designed marketing materials for club training day",
        "Redesigned member check-in page for design system consistency"
      ],
      technologies: ["Customer Experience", "Design Systems", "Marketing Design", "UX Optimization"],
      icon: "💪"
    },
    {
      id: "kgs-ux",
      year: "Oct 2021 - March 2023",
      title: "UX Designer",
      company: "Koniag Government Services",
      type: "experience",
      description: "Led UX design initiatives for Department of Labor, focusing on complex government workflows, form optimization, and user experience improvements for time-sensitive documentation processes.",
      achievements: [
        "Created workflows that decreased turnaround time for time-sensitive documents",
        "Analyzed and redesigned flows to optimize user experience",
        "Collaborated with designers and developers on design system integrity",
        "Improved correspondence efficiency between government users"
      ],
      technologies: ["User Research", "Workflow Design", "Government UX", "Design Systems"],
      icon: "🏛️"
    },
    {
      id: "shift-nudge",
      year: "Nov 2022 - March 2023",
      title: "Interface Design Certificate",
      company: "Shift Nudge",
      type: "education",
      description: "Advanced interface design training focusing on modern UI patterns, visual hierarchy, and contemporary design systems during UX role at Koniag.",
      achievements: [
        "Mastered advanced interface design principles",
        "Learned modern UI component architecture",
        "Developed expertise in visual design systems",
        "Applied learning directly to government UX projects"
      ],
      technologies: ["Interface Design", "Visual Hierarchy", "UI Components", "Design Systems"],
      icon: "🎨"
    },
    {
      id: "future-fellow",
      year: "Nov 2020 - Nov 2021",
      title: "Made in the Future Fellow",
      company: "Tomorrow Looks Bright",
      type: "experience",
      description: "Selected as one of 38 fellows mentored by leading design industry professionals including Wes O&apos;Haire, Kristy Tillman, and Michael Bierut. Built community connections and advanced design thinking skills.",
      achievements: [
        "1 of 38 fellows selected for exclusive mentorship program",
        "Mentored by industry leaders including Michael Bierut",
        "Built community of designers for idea sharing and feedback",
        "Developed network of design industry resources and connections"
      ],
      technologies: ["Design Leadership", "Community Building", "Mentorship", "Design Strategy"],
      icon: "🌟"
    },
    {
      id: "saveaway-intern",
      year: "July 2020 - Feb 2021",
      title: "Product Design Intern",
      company: "SaveAway® with INSPIRAVE",
      type: "experience",
      description: "Contributed to product design and development, implementing design systems and facilitating design sprints. Helped take platform from beta to full launch with data-driven design decisions.",
      achievements: [
        "Implemented design system for platform consistency",
        "Facilitated design sprints for problem identification",
        "Utilized user analytics to verify design effectiveness",
        "Successfully took platform out of beta to full launch"
      ],
      technologies: ["Product Design", "Design Systems", "Design Sprints", "User Analytics"],
      icon: "💳"
    },
    {
      id: "scite-ux",
      year: "May 2019",
      title: "UX Designer",
      company: "scite_",
      type: "experience",
      description: "Conducted comprehensive user research for academic research platform, leading to development of browser extension and platform improvements that enhanced researcher workflows.",
      achievements: [
        "Conducted user interviews to discover research habits",
        "Performed usability testing of current platform",
        "Synthesized data into actionable insights",
        "Led to development of workflow-enhancing browser extension"
      ],
      technologies: ["User Research", "Usability Testing", "Academic Research", "Browser Extensions"],
      icon: "🔬"
    },
    {
      id: "general-assembly",
      year: "Feb 2019 - May 2019",
      title: "User Experience Design Immersive Fellow",
      company: "General Assembly",
      type: "education", 
      description: "Intensive UX design program covering complete design process from discovery through testing. Collaborated with developers on responsive websites and managed team workflows.",
      achievements: [
        "Completed comprehensive UX design methodology training",
        "Mastered discovery, research, wireframing, and prototyping",
        "Analyzed information architecture and user flows",
        "Collaborated with developers on responsive client website"
      ],
      technologies: ["UX Research", "Wireframing", "Prototyping", "Information Architecture"],
      icon: "🎓"
    },
    {
      id: "penn-state",
      year: "2012 - 2016",
      title: "Bachelor of Science in Marketing",
      company: "Penn State University",
      type: "education", 
      description: "Comprehensive business marketing education providing foundation in consumer psychology, market research, and strategic business thinking that informs user-centered design approach.",
      achievements: [
        "Bachelor&apos;s degree in Marketing with business focus",
        "Developed understanding of consumer psychology",
        "Learned market research methodologies",
        "Built strategic thinking and business acumen foundation"
      ],
      technologies: ["Marketing Strategy", "Consumer Psychology", "Market Research", "Business Strategy"],
      icon: "📚"
    }
  ];

  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const itemId = entry.target.dataset.itemId;
          setVisibleItems(prev => [...new Set([...prev, itemId])]);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.3
    });

    const timelineItems = timelineRef.current?.querySelectorAll('.timeline-item');
    timelineItems?.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const getTypeStyles = (type) => {
    const styles = {
      current: {
        border: "border-blue-200",
        bg: "bg-blue-50", 
        iconBg: "bg-blue-500",
        titleColor: "text-blue-800"
      },
      experience: {
        border: "border-green-200",
        bg: "bg-green-50",
        iconBg: "bg-green-500", 
        titleColor: "text-green-800"
      },
      education: {
        border: "border-purple-200",
        bg: "bg-purple-50",
        iconBg: "bg-purple-500",
        titleColor: "text-purple-800"
      }
    };
    return styles[type] || styles.experience;
  };

  return (
    <section className="section-padding bg-white">
      <div className="container-narrow">
        <div className="text-center mb-16">
          <h2 className="heading-section animate-fade-in">
            Professional Journey
          </h2>
          <p className="text-body max-w-3xl mx-auto animate-fade-in">
            A timeline of my growth from business marketing foundation to UX design expertise, 
            combining strategic business thinking with user-centered design principles.
          </p>
        </div>

        <div className="relative" ref={timelineRef}>
          {/* Vertical line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-green-200 to-purple-200 transform md:-translate-x-0.5"></div>

          <div className="space-y-12">
            {timelineData.map((item, index) => {
              const styles = getTypeStyles(item.type);
              const isVisible = visibleItems.includes(item.id);
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={item.id}
                  className={`timeline-item relative flex items-center ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                  data-item-id={item.id}
                >
                  {/* Content Card */}
                  <div className={`flex-1 ml-12 md:ml-0 ${
                    isLeft ? 'md:mr-8' : 'md:ml-8'
                  }`}>
                    <div className={`
                      card-professional p-8 border-2 transition-all duration-500
                      ${styles.border} ${styles.bg}
                      ${isVisible ? 'animate-scale-in opacity-100' : 'opacity-0'}
                    `} style={{ 
                      animationDelay: `${index * 0.2}s`,
                      transform: isVisible ? 'translateY(0)' : 'translateY(30px)'
                    }}>
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-2xl">{item.icon}</span>
                            <span className={`font-bold text-sm ${styles.titleColor}`}>
                              {item.year}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {item.title}
                          </h3>
                          <p className="text-gray-600 font-semibold">
                            {item.company}
                          </p>
                        </div>
                        
                        {item.type === 'current' && (
                          <span className="status-badge-info animate-pulse-slow">
                            Current
                          </span>
                        )}
                      </div>

                      {/* Description */}
                      <p className="text-body mb-6">
                        {item.description}
                      </p>

                      {/* Achievements */}
                      <div className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Key Achievements:
                        </h4>
                        <ul className="space-y-2">
                          {item.achievements.map((achievement, achIndex) => (
                            <li key={achIndex} className="flex items-start space-x-2">
                              <span className="text-green-500 mt-1">✓</span>
                              <span className="text-gray-700 text-sm">
                                {achievement}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Technologies */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">
                          Skills & Focus:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {item.technologies.map((tech, techIndex) => (
                            <span
                              key={techIndex}
                              className="skill-badge"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>


                </div>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16 p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            What&apos;s Next?
          </h3>
          <p className="text-body mb-6 max-w-2xl mx-auto">
            I&apos;m excited to bring my unique combination of business strategy, customer experience, 
            and UX design expertise to new challenges. Let&apos;s create something amazing together.
          </p>
          <div className="space-y-2">
            <p className="text-gray-700 font-semibold">
              Available for new opportunities
            </p>
            <p className="text-sm text-gray-500">
              Remote • New York City • Full-time & Contract
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalTimeline;